/**
 * Logo component for MAGreen
 */

import logoData from '@/data/logo.json';

interface LogoProps {
  className?: string;
  variant?: 'full' | 'icon' | 'text' | 'navbar' | 'footer' | 'hero';
  style?: React.CSSProperties;
}

export function Logo({ className = '', variant = 'full', style }: LogoProps) {
  // Select the appropriate logo data based on variant
  const getLogoData = () => {
    if (variant === 'navbar') return logoData.navbar;
    if (variant === 'footer') return logoData.footer;
    if (variant === 'hero') return logoData.heroIcon;
    // For backward compatibility, use navbar data for 'full', 'icon', 'text'
    return logoData.navbar;
  };

  const data = getLogoData() as any;

  if (variant === 'text' || data.type === 'text') {
    return (
      <div className={`font-bold text-2xl ${className}`} style={style}>
        {data.text1 && (
          <span className="gradient-text">{data.text1}</span>
        )}
        {data.text2 && (
          <span className="text-gray-800">{data.text2}</span>
        )}
      </div>
    );
  }

  if (variant === 'icon' || variant === 'hero') {
    if (data.type === 'image' && data.image) {
      return (
        <div className={`relative ${className}`} style={style}>
          <img src={data.image} alt={data.alt} style={{ width: variant === 'hero' ? '25rem' : '3rem', height: 'auto', objectFit: 'contain' }} />
        </div>
      );
    }
    return (
      <div className={`relative ${variant === 'hero' ? 'w-24 h-24' : 'w-12 h-12'} ${className}`} style={style}>
        <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="50" fill="#84cc16" />
          <path d="M50 20 C50 20, 70 35, 70 50 C70 65, 50 80, 50 80" stroke="white" strokeWidth="4" fill="none" />
          <path d="M50 20 C50 20, 30 35, 30 50 C30 65, 50 80, 50 80" stroke="white" strokeWidth="4" fill="none" />
          <path d="M30 50 L70 50" stroke="white" strokeWidth="4" />
        </svg>
      </div>
    );
  }

  // Full variant (navbar, footer, or default)
  if (data.type === 'image' && data.image) {
    return (
      <div className={`flex items-center ${className}`} style={style}>
        <img
          src={data.image}
          alt={data.alt}
          style={{
            maxHeight: variant === 'footer' ? '3rem' : '4.5rem',
            width: 'auto',
            objectFit: 'contain',
          }}
          className="logo-img"
        />
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`} style={style}>
      <div className="relative w-10 h-10">
        <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="50" fill="#84cc16" />
          <path d="M50 20 C50 20, 70 35, 70 50 C70 65, 50 80, 50 80" stroke="white" strokeWidth="4" fill="none" />
          <path d="M50 20 C50 20, 30 35, 30 50 C30 65, 50 80, 50 80" stroke="white" strokeWidth="4" fill="none" />
          <path d="M30 50 L70 50" stroke="white" strokeWidth="4" />
        </svg>
      </div>
      {data.text1 && data.text2 && (
        <span className="font-bold text-xl">
          <span className="gradient-text">{data.text1}</span>
          <span className="text-gray-800">{data.text2}</span>
        </span>
      )}
    </div>
  );
}
